import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { X, Calculator } from 'lucide-react';

const STORAGE_KEY = 'fundle_exit_intent_shown_v1';
const PATH_BLOCKLIST = ['/contact', '/leads', '/admin', '/downloads', '/roi-calculator'];

/**
 * Exit-intent popup that fires when the user moves the cursor toward the
 * browser chrome (top of viewport) on desktop. Suppressed on mobile, on
 * blocked routes, and after first dismissal (localStorage).
 */
const ExitIntentPopup = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    // Skip on mobile / touch devices
    if (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) return undefined;
    // Skip on blocked paths
    if (PATH_BLOCKLIST.some((p) => location.pathname.startsWith(p))) return undefined;
    // Already shown this session
    try { if (sessionStorage.getItem(STORAGE_KEY)) return undefined; } catch { /* noop */ }

    let armedAt = Date.now();
    const minDwell = 15000; // require 15s on page first

    const onMouseOut = (e) => {
      if (Date.now() - armedAt < minDwell) return;
      // Only trigger if mouse leaves toward top
      if (e.clientY > 5) return;
      if (e.relatedTarget || e.toElement) return;
      setOpen(true);
      try { sessionStorage.setItem(STORAGE_KEY, '1'); } catch { /* noop */ }
      document.removeEventListener('mouseout', onMouseOut);
    };

    document.addEventListener('mouseout', onMouseOut);
    return () => document.removeEventListener('mouseout', onMouseOut);
  }, [location.pathname]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center px-4 bg-[#0B1437]/60 backdrop-blur-sm" data-testid="exit-intent">
      <div className="w-full max-w-lg bg-white rounded-3xl shadow-2xl p-8 relative">
        <button onClick={() => setOpen(false)} className="absolute top-4 right-4 text-slate-400 hover:text-[#1F2A4A]" aria-label="Close" data-testid="exit-intent-close">
          <X className="w-5 h-5" />
        </button>
        <div className="w-14 h-14 rounded-2xl bg-[#0B1437] text-white flex items-center justify-center mb-5">
          <Calculator className="w-6 h-6" />
        </div>
        <h2 className="text-2xl font-bold text-[#0B1437] leading-snug">Before you go — how much is loyalty really worth to your business?</h2>
        <p className="mt-3 text-[#4A5878]">
          Take 60 seconds with our free ROI calculator. Get the incremental revenue, programme cost and net return on a typical retail loyalty programme — plus the Loyalty ROI Playbook PDF.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link
            to="/roi-calculator"
            onClick={() => setOpen(false)}
            className="px-5 py-2.5 rounded-full bg-[#0B1437] text-white text-sm font-semibold hover:bg-[#050A1F] transition"
            data-testid="exit-intent-cta"
          >
            Try the ROI calculator
          </Link>
          <button
            onClick={() => setOpen(false)}
            className="px-5 py-2.5 rounded-full bg-slate-100 text-[#4A5878] text-sm font-medium hover:bg-slate-200 transition"
            data-testid="exit-intent-dismiss"
          >
            No thanks
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExitIntentPopup;
